import { CHART_NO_FOCUS_OUTLINE, DEFAULT_CHART_HEIGHT } from '@orn/shared-web/components/Chart';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import AnimationFixToolTip from '@orn/shared-web/components/Tooltip/AnimationFixToolTip';
import * as React from 'react';
import { Area, ComposedChart, CartesianGrid, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { useContainerWidth } from '../../../hooks/useContainerWidth';
import { formatMoneyShort, formatOrdinal } from '../../../utils/formatting';
import ChangeIndicator from '../../changeIndicators/ChangeIndicator';
import { ChartCard } from '../../charts/ChartCard';
import { ChartHeader } from '../../charts/ChartHeader';
import { ChartSeriesLineSwatch, ChartSeriesTooltip, ChartSeriesTooltipRow } from '../../charts/ChartSeriesTooltip';

import { processDailyCumulativeSpendData } from './dataProcessing';

import type { IDailyCumulativeSpendDataPoint } from './dataProcessing';
import type { ColumnTable } from 'arquero';

/**
 * Custom tooltip content for DashboardMonthlySpendComparisonChart
 * Shows cumulative spending for current month and last month at the hovered day
 */
const DashboardMonthlySpendComparisonChartTooltipContent = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number | null;
    dataKey: string;
    payload: IDailyCumulativeSpendDataPoint;
  }>;
}) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const dataPoint = payload[0]?.payload;
  if (!dataPoint) {
    return null;
  }

  const dayLabel = `Day ${dataPoint.day}`;
  const currentSpending = dataPoint.spending !== null ? formatMoneyShort(dataPoint.spending) : null;
  const previousSpending = dataPoint.previousSpending !== null ? formatMoneyShort(dataPoint.previousSpending) : null;

  // Don't show tooltip if both values are null
  if (currentSpending === null && previousSpending === null) {
    return null;
  }

  return (
    <ChartSeriesTooltip title={dayLabel} titleTransform="uppercase">
      {currentSpending !== null && (
        <ChartSeriesTooltipRow
          swatch={<ChartSeriesLineSwatch className="text-utility-indigo-500" />}
          label="This month"
          value={currentSpending}
        />
      )}
      {previousSpending !== null && (
        <ChartSeriesTooltipRow
          swatch={<ChartSeriesLineSwatch className="text-utility-indigo-300" style="dotted" />}
          label="Last month"
          value={previousSpending}
        />
      )}
    </ChartSeriesTooltip>
  );
};

export interface IDashboardMonthlySpendComparisonChartProps {
  transactions: ColumnTable | null;
  className?: string;
  isLoading?: boolean;
}

/**
 * Dashboard card that compares this month's cumulative spending against the
 * previous month. The current month is drawn as a solid indigo area chart that
 * stops at today and carries a static marker at the last data point; the
 * previous month is overlaid as a dotted line spanning the full month for
 * comparison. Renders a loading indicator while fetching and a "no data"
 * placeholder when the supplied transactions table is null.
 */
const DashboardMonthlySpendComparisonChart = ({
  transactions,
  className = '',
  isLoading = false,
}: IDashboardMonthlySpendComparisonChartProps) => {
  const { containerRef } = useContainerWidth();

  // Process the data to get daily cumulative spending
  const { chartData, currentTotal, previousTotal, xAxisTicks } = React.useMemo(() => {
    if (!transactions) {
      return {
        chartData: [],
        currentTotal: 0,
        previousTotal: 0,
        xAxisTicks: [1, 9, 16, 24, 31],
      };
    }

    const result = processDailyCumulativeSpendData(transactions);

    // Calculate max day as maximum of current day and last month's total days
    const maxDay = Math.max(result.currentMonthDaysElapsed, result.lastMonthTotalDays);

    // Filter chart data to only include days up to maxDay
    const filteredChartData = result.chartData.filter((d) => d.day <= maxDay);

    // Build x-axis ticks with dynamic last value
    const baseTicks = [1, 9, 16, 24];
    const xAxisTicks = maxDay > 24 ? [...baseTicks, maxDay] : baseTicks.filter((t) => t <= maxDay);

    return {
      chartData: filteredChartData,
      currentTotal: result.currentTotal,
      previousTotal: result.previousTotal,
      xAxisTicks,
    };
  }, [transactions]);

  const lastCurrentSpendingIndex = React.useMemo(() => {
    for (let i = chartData.length - 1; i >= 0; i--) {
      if (chartData[i]?.spending !== null) {
        return i;
      }
    }
    return -1;
  }, [chartData]);

  return (
    <ChartCard
      containerRef={containerRef}
      className={className}
      header={
        <ChartHeader title="Spending this month" value={currentTotal}>
          {previousTotal > 0 && (
            <ChangeIndicator
              current={currentTotal}
              previous={previousTotal}
              versusText="vs this time last month"
              increaseGood={false}
            />
          )}
        </ChartHeader>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center" style={{ height: `${DEFAULT_CHART_HEIGHT}px` }}>
          <LoadingIndicator size="md" />
        </div>
      ) : chartData.length === 0 ? (
        <div className="text-tertiary flex items-center justify-center" style={{ height: `${DEFAULT_CHART_HEIGHT}px` }}>
          No spending data available
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={DEFAULT_CHART_HEIGHT} className={CHART_NO_FOCUS_OUTLINE}>
            <ComposedChart
              data={chartData}
              margin={{
                left: -10,
                right: 20,
                top: 5,
                bottom: 5,
              }}
              className="text-tertiary [&_.recharts-text]:text-xs"
            >
              <defs>
                <linearGradient id="spendingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="currentColor" className="text-utility-indigo-500" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="currentColor" className="text-utility-indigo-500" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid
                vertical={false}
                horizontal={true}
                stroke="currentColor"
                strokeDasharray="0"
                className="text-utility-gray-100"
              />

              <XAxis
                fill="currentColor"
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tickMargin={12}
                ticks={xAxisTicks}
                tickFormatter={formatOrdinal}
              />

              <YAxis
                fill="currentColor"
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                width={50}
                tickFormatter={(value: number) => formatMoneyShort(value, { precision: 'whole' })}
              />

              <AnimationFixToolTip
                content={<DashboardMonthlySpendComparisonChartTooltipContent />}
                cursor={{
                  className: 'stroke-utility-indigo-500 stroke-2',
                }}
              />

              {/* Previous month spending - Dotted line (rendered first so it's behind) */}
              <Line
                isAnimationActive={false}
                dataKey="previousSpending"
                name="Last Month"
                type="monotone"
                stroke="currentColor"
                strokeWidth={3}
                strokeDasharray="0 8"
                strokeLinecap="round"
                className="text-utility-indigo-300"
                dot={false}
                connectNulls={false}
                activeDot={{
                  className: 'fill-bg-primary stroke-utility-indigo-300 stroke-2',
                  r: 4,
                }}
              />

              {/* Current month spending - Filled area chart */}
              <Area
                isAnimationActive={false}
                dataKey="spending"
                name="This Month"
                type="monotone"
                stroke="currentColor"
                strokeWidth={3}
                className="text-utility-indigo-500"
                fill="url(#spendingGradient)"
                fillOpacity={1}
                connectNulls={false}
                dot={(dotProps: { cx?: number; cy?: number; index?: number; key?: React.Key | null }) => {
                  const { cx: dotCx, cy: dotCy, index, key } = dotProps;
                  const dotKey = key ?? `dot-${index ?? 'unknown'}`;
                  if (index !== lastCurrentSpendingIndex || dotCx === undefined || dotCy === undefined) {
                    return <g key={dotKey} />;
                  }
                  return (
                    <circle
                      key={dotKey}
                      cx={dotCx}
                      cy={dotCy}
                      r={5}
                      className="fill-bg-primary stroke-utility-indigo-500"
                      strokeWidth={2}
                    />
                  );
                }}
                activeDot={{
                  className: 'fill-bg-primary stroke-utility-indigo-500 stroke-2',
                  r: 5,
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Legend */}
          <div className="mt-4 flex items-center justify-center gap-6">
            <div className="flex items-center gap-2">
              <svg width="20" height="6" className="text-utility-indigo-500">
                <line x1="0" y1="3" x2="20" y2="3" stroke="currentColor" strokeWidth="3" />
              </svg>
              <p className="text-tertiary text-sm">This month</p>
            </div>
            <div className="flex items-center gap-2">
              <svg width="20" height="6" className="text-utility-indigo-300">
                <line
                  x1="0"
                  y1="3"
                  x2="20"
                  y2="3"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="0 8"
                  strokeLinecap="round"
                />
              </svg>
              <p className="text-tertiary text-sm">Last month</p>
            </div>
          </div>
        </>
      )}
    </ChartCard>
  );
};

export default DashboardMonthlySpendComparisonChart;
